import React from "react";
import PropTypes from "prop-types";
import { Box, Divider, Grid } from "@mui/material";
import { useSelector } from "react-redux";

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/ar-sa";

dayjs.extend(customParseFormat);
import CheckInMargins from "../CheckInMargins";
import messages from "../../../../../assets/locale/messages";
import UniformCodeChip from "../UniformCodeChip";
import ShiftField from "./ShiftField";
import PaidTrainingSession from "../PaidTrainingSession";
import { isPaidTrainingToggleOn } from "../../../../../utils/paidTrainingRemoval";
import "../../_EventDetailFields.scss";

const WorkingShifts = ({ shifts, uniformCode, portalVariant }) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared } = messages[lang];
  const t = events.viewEvents;
  // The company's internal cost figure is never exposed to the client.
  const showTotalPayroll = portalVariant !== "client-portal";
  const fieldMd = showTotalPayroll ? 3 : 4;

  const formatTime = (t) => {
    if (!t) return "—";
    const parsed = dayjs(t, "HH:mm").locale(lang === "ar" ? "ar-sa" : "en");
    return parsed.isValid() ? parsed.format("hh:mm A") : "—";
  };

  const formatTimeSlot = (start, end) => {
    if (!start && !end) return "—";
    return `${formatTime(start)} - ${formatTime(end)}`;
  };

  const formatShiftType = (shiftType) =>
    shared?.shiftTypes?.[shiftType] ||
    (shiftType
      ? shiftType
          .split("_")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")
      : "—");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <span className="event-detail-section-title">{t.workingShifts}</span>

      {(shifts || []).map((shift, idx) => {
        const trainingForView =
          shift?.training_attributes ?? shift?.training;

        return (
          <Box
            key={shift?.id ?? idx}
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
          <Grid container spacing={3}>
            <ShiftField label={t.shiftType} md={fieldMd}>
              {formatShiftType(shift?.shift_type)}
            </ShiftField>

            {showTotalPayroll && (
              <ShiftField label={t.totalPayroll} accent md={fieldMd}>
                {shift?.payroll_amount != null
                  ? `${t.sar} ${shift.payroll_amount}`
                  : "—"}
              </ShiftField>
            )}

            <ShiftField label={t.timeSlot} md={fieldMd}>
              {formatTimeSlot(shift?.starts_at, shift?.ends_at)}
            </ShiftField>

            <ShiftField label={t.uniformCode} md={fieldMd}>
              <UniformCodeChip code={uniformCode} noneLabel={t.none} />
            </ShiftField>
          </Grid>

          <Divider sx={{ borderColor: "var(--color-muted-default)" }} />

          <CheckInMargins shift={shift} />

          {isPaidTrainingToggleOn(trainingForView) && (
            <PaidTrainingSession
              training={trainingForView}
              portalVariant={portalVariant}
            />
          )}

          {idx < (shifts?.length ?? 1) - 1 && <Divider sx={{ mt: 3 }} />}
        </Box>
        );
      })}
    </Box>
  );
};

WorkingShifts.propTypes = {
  shifts: PropTypes.arrayOf(PropTypes.object),
  uniformCode: PropTypes.string,
  portalVariant: PropTypes.oneOf(["company-portal", "client-portal"]),
};

WorkingShifts.defaultProps = {
  shifts: [],
  uniformCode: "",
  portalVariant: "company-portal",
};

export default WorkingShifts;
